// hooks/useCompanyLogic.js
import { useState, useEffect } from 'react';
import companyApi from 'services/api/companyApi';
import { useAuth } from 'components/ui/navigationGuard';
import Icon from 'components/AppIcon';

const useCompanyLogic = () => {
  const { user } = useAuth();
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    company: null,
    mode: 'add',
  });
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    is_active: true,
  });
  const [errors, setErrors] = useState({});

  // --- Notification Functions ---
  const closeNotification = () => setNotification(null);
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(closeNotification, 3000);
  };

  // --- Data Loading ---
  const loadCompanies = async () => {
    try {
      setLoading(true);
      const companiesData = await companyApi.getCompanies();
      setCompanies(companiesData);
    } catch (error) {
      console.error("Error fetching companies:", error);
      showNotification("Failed to fetch companies", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  // --- Form Handling ---
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    if (!formData.name.trim()) validationErrors.name = "Company name is required";
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (modalState.mode === 'add') {
        await companyApi.createCompany(formData);
        showNotification("Company created successfully", "success");
      } else {
        await companyApi.updateCompany(modalState.company.id, formData);
        showNotification("Company updated successfully", "success");
      }
      setModalState({ isOpen: false, company: null, mode: 'add' });
      loadCompanies();
    } catch (error) {
      console.error("Error saving company:", error);
      const errorMessage = error.response?.data?.detail || "Failed to save company";
      showNotification(errorMessage, "error");
    }
  };

  const handleAddCompany = () => {
    // Fixed permission check
    if (user.role !== "super_admin") {
      showNotification("You do not have permission to create companies", "error");
      return;
    }
    setFormData({ name: '', code: '', address: '', contact_email: '', contact_phone: '', is_active: true });
    setErrors({});
    setModalState({ isOpen: true, company: null, mode: 'add' });
  };

  const handleEditCompany = (company) => {
    // Fixed permission check
    if (user.role !== "super_admin" && user.company_id !== company.id) {
      showNotification("You do not have permission to edit this company", "error");
      return;
    }
    setFormData({
      name: company.name,
      code: company.code || '',
      address: company.address || '',
      contact_email: company.contact_email || '',
      contact_phone: company.contact_phone || '',
      is_active: company.is_active,
    });
    setErrors({});
    setModalState({ isOpen: true, company, mode: 'edit' });
  };

  const handleDeleteCompany = async (company) => {
    // Fixed permission check
    if (user.role !== "super_admin") {
      showNotification("You do not have permission to delete companies", "error");
      return;
    }
    if (window.confirm(`Are you sure you want to delete "${company.name}"? This action cannot be undone.`)) {
      try {
        await companyApi.deleteCompany(company.id);
        showNotification("Company deleted successfully", "success");
        loadCompanies();
      } catch (error) {
        console.error("Error deleting company:", error);
        const errorMessage = error.response?.data?.detail || "Failed to delete company";
        showNotification(errorMessage, "error");
      }
    }
  };

  // --- Table Columns Definition ---
  const columns = [
    {
      header: 'Company Name',
      accessorKey: 'name',
      cell: (info) => <span className="font-medium text-slate-900">{info?.name}</span>,
    },
    {
      header: 'Code',
      accessorKey: 'code',
      cell: (info) => <span className="text-slate-600">{info?.code || 'N/A'}</span>,
    },
    {
      header: 'Status',
      accessorKey: 'is_active',
      cell: (info) => (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          info?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {info?.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: (info) => {
        const company = info?.row?.original;
        const canEdit = user.role === "super_admin" || user.company_id === company.id;
        const canDelete = user.role === "super_admin";
        
        return (
          <div className="flex space-x-2">
            {canEdit && (
              <button
                onClick={() => handleEditCompany(company)}
                className="text-indigo-600 hover:text-indigo-900"
                title="Edit"
              >
                <Icon name="Edit" size={16} />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => handleDeleteCompany(company)}
                className="text-red-600 hover:text-red-900"
                title="Delete"
              >
                <Icon name="Trash" size={16} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return {
    // State
    isNavCollapsed,
    companies,
    loading,
    modalState,
    notification,
    formData,
    errors,
    columns,

    // State setters
    setIsNavCollapsed,
    setModalState,

    // Functions
    closeNotification,
    showNotification,
    loadCompanies,
    handleInputChange,
    validateForm,
    handleSave,
    handleAddCompany,
    handleEditCompany,
    handleDeleteCompany,
  };
};

export default useCompanyLogic;

// // components/ui/Icon.js
// const Icon = ({ name, size = 20, className = "" }) => {
//   const icons = {
//     Edit: (
//       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
//         <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
//         <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
//       </svg>
//     ),
//     Trash: (
//       <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
//         <polyline points="3 6 5 6 21 6"></polyline>
//         <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
//       </svg>
//     ),
//   };

//   return icons[name] || null;
// };

// export default Icon;
